import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import VideocamIcon from "@mui/icons-material/Videocam";
import PropTypes from "prop-types";

const NAVY = "#292E5C";

const SuccessIcon = () => (
  <Box
    aria-hidden="true"
    sx={{
      width: 172,
      height: 172,
      position: "relative",
      display: "grid",
      placeItems: "center",
    }}
  >
    {[
      { left: 26, top: 16, width: 9, height: 28, rotate: -22, color: "#17B77A" },
      { right: 24, top: 22, width: 9, height: 28, rotate: 24, color: "#17B77A" },
      { left: 18, bottom: 44, width: 7, height: 22, rotate: 54, color: "#F59E0B" },
      { right: 18, bottom: 45, width: 7, height: 22, rotate: -48, color: "#F59E0B" },
      { left: 34, top: 62, width: 10, height: 10, radius: 999, color: "#F5B335" },
      { right: 36, top: 68, width: 9, height: 9, radius: 999, color: "#6EDCB8" },
    ].map((shape, index) => (
      <Box
        key={index}
        sx={{
          position: "absolute",
          left: shape.left,
          right: shape.right,
          top: shape.top,
          bottom: shape.bottom,
          width: shape.width,
          height: shape.height,
          borderRadius: shape.radius || 2,
          bgcolor: shape.color,
          transform: `rotate(${shape.rotate || 0}deg)`,
          transformOrigin: "center",
        }}
      />
    ))}

    <Box
      sx={{
        width: 172,
        height: 172,
        borderRadius: "50%",
        bgcolor: "rgba(159, 212, 192, 0.20)",
        position: "absolute",
      }}
    />
    <Box
      sx={{
        width: 136,
        height: 136,
        borderRadius: "50%",
        bgcolor: "rgba(159, 212, 192, 0.40)",
        position: "absolute",
      }}
    />
    <Box
      sx={{
        width: 104,
        height: 104,
        borderRadius: "50%",
        background: "linear-gradient(180deg, #53A898 0%, #1E7E6C 100%)",
        position: "absolute",
        display: "grid",
        placeItems: "center",
        boxShadow: "0 18px 40px rgba(30, 126, 108, 0.25)",
      }}
    >
      <CheckIcon
        sx={{
          color: "#fff",
          fontSize: 66,
          stroke: "#fff",
          strokeWidth: 1.2,
        }}
      />
    </Box>
  </Box>
);

const CouncilBookingSuccessDialog = ({ booking, onClose, open }) => {
  const appointment = booking?.appointment || {};
  const meetLink = appointment.google_meet_link || "";
  const calendarLink = appointment.google_event_link || "";
  const pendingVerification = Boolean(booking?.pendingVerification);
  const headerTitle = pendingVerification
    ? "Payment Received"
    : "Booking Confirmation";
  const title = pendingVerification ? "Almost There" : "Booking Successful";
  const subtitle = pendingVerification
    ? "We've received your payment and are confirming your booking. You'll be notified once it's confirmed."
    : "Your booking has been completed successfully.";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        sx: {
          bgcolor: "rgba(0, 0, 0, 0.45)",
          backdropFilter: "blur(6px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", sm: 589 },
          maxWidth: "calc(100% - 24px)",
          borderRadius: "20px",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0px 16px 75px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <Box
        sx={{
          alignSelf: "stretch",
          px: { xs: 2.5, sm: 5 },
          py: { xs: 2.25, sm: 3 },
          position: "relative",
          bgcolor: NAVY,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: 21, sm: 24 },
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              lineHeight: "40px",
            }}
          >
            {headerTitle}
          </Typography>
        </Box>

        <IconButton
          type="button"
          aria-label="Close booking confirmation"
          onClick={onClose}
          sx={{
            width: 32,
            height: 32,
            color: "#fff",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.12)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          alignSelf: "stretch",
          px: { xs: 2.5, sm: 5 },
          py: { xs: 3.5, sm: 4 },
          background: " #FFFFFF",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2.25,
          textAlign: "center",
        }}
      >
        <SuccessIcon />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              color: "#000",
              fontSize: { xs: 28, sm: 36 },
              fontFamily: "Poppins, sans-serif",
              fontWeight: 700,
              lineHeight: 1.12,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: "#687386",
              fontSize: { xs: 15, sm: 18 },
              fontFamily: "Poppins, sans-serif",
              fontWeight: 400,
              lineHeight: "26px",
            }}
          >
            {subtitle}
          </Typography>
          {booking ? (
            <Typography
              sx={{
                mt: 0.5,
                maxWidth: 420,
                color: "#4B5563",
                fontSize: 13,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                lineHeight: "22px",
              }}
            >
              {booking.dateLabel} at {booking.slotLabel}
            </Typography>
          ) : null}
          
        </Box>
      </Box>

      <Box
        sx={{
          alignSelf: "stretch",
          px: { xs: 2.5, sm: 5 },
          pt: 2.5,
          pb: { xs: 2.5, sm: 3.5 },
          borderTop: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          bgcolor: "#fff",
        }}
      >
        <Button
          type="button"
          onClick={onClose}
          sx={{
            minWidth: 89,
            height: 46,
            px: 3,
            borderRadius: "10px",
            bgcolor: NAVY,
            color: "#fff",
            textTransform: "none",
            fontSize: 16,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#20264F",
              boxShadow: "0 10px 22px rgba(41, 46, 92, 0.18)",
            },
          }}
        >
          OK
        </Button>
      </Box>
    </Dialog>
  );
};

CouncilBookingSuccessDialog.propTypes = {
  booking: PropTypes.shape({
    appointment: PropTypes.shape({
      google_event_link: PropTypes.string,
      google_meet_link: PropTypes.string,
    }),
    dateLabel: PropTypes.string,
    slotLabel: PropTypes.string,
    pendingVerification: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

CouncilBookingSuccessDialog.defaultProps = {
  booking: null,
};

export default CouncilBookingSuccessDialog;
